import useAsyncFn from 'react-use/lib/useAsyncFn';
import { useApi } from '@backstage/core-plugin-api';
import {
  Alert,
  Button,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  Text,
} from '@backstage/ui';
import { sonarqubeApiRef } from '../../../api/sonarqube-api';
import styles from './SonarQubeServiceGroupPage.module.css';

interface DeleteServiceGroupDialogProps {
  serviceGroupId: string;
  name: string | undefined;
  open: boolean;
  onClose: () => void;
  onDeleted: () => void;
}

export const DeleteServiceGroupDialog = ({
  serviceGroupId,
  name,
  open,
  onClose,
  onDeleted,
}: DeleteServiceGroupDialogProps) => {
  const sonarqubeApi = useApi(sonarqubeApiRef);
  const [submitState, submit] = useAsyncFn(async () => {
    await sonarqubeApi.deleteServiceGroup(serviceGroupId);
    onDeleted();
  }, [sonarqubeApi, serviceGroupId, onDeleted]);

  const close = () => {
    if (!submitState.loading) onClose();
  };

  return (
    <Dialog
      isOpen={open}
      onOpenChange={isOpen => {
        if (!isOpen) close();
      }}
      width={480}
    >
      <DialogHeader>
        Are you sure you want to delete {name ? `"${name}"` : 'this tooling group'}?
      </DialogHeader>
      <DialogBody className={styles.dialogBody}>
        <Text as="p" color="secondary">
          This removes the tooling group with its members and SonarQube
          groups. This cannot be undone.
        </Text>
        <Alert
          status="warning"
          icon
          description="Its SonarQube projects are not deleted but orphaned: each project's key changes to orphaned_{repository} and it stays bound to its repository. The SonarQube analysis workflow and secrets stay in the repositories, so analysis continues on the orphaned projects. Setting up a project for the same repository later adopts the orphan again."
        />
        {submitState.error && (
          <Text color="danger" as="p">
            {submitState.error.message}
          </Text>
        )}
      </DialogBody>
      <DialogFooter>
        <Button
          variant="secondary"
          onClick={close}
          isDisabled={submitState.loading}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          destructive
          onClick={submit}
          isDisabled={submitState.loading}
          isPending={submitState.loading}
        >
          Delete tooling group
        </Button>
      </DialogFooter>
    </Dialog>
  );
};
